import {
  BreakpointsEnum,
  isBreakpointDown,
  isBreakpointUp,
  useBreakpoint,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useSelector} from 'Provider';
import {Routes} from 'models/Navigation';

import {selectIsListView} from 'store/offers/offers.selectors';

export enum OfferCardSize {
  NANO = 'NANO',
  SM = 'SM',
  MD = 'MD',
  LG = 'LG',
}

export interface OfferCardThemeProperties {
  mainPadding: number;
  titleFontSize: string;
  width: string;
  height: string;
  titleHeight: string;
  titleLineHeight: string;
  propertiesCharLimit: number;
  propertiesFontSize: number;
  statusFontSize: number;
  expiryMargin: number;
  cardSize: OfferCardSize;
}

type OfferCardTheme = {
  [key in OfferCardSize]: OfferCardThemeProperties;
};

const offerCardThemeOffersScreen: OfferCardTheme = {
  [OfferCardSize.NANO]: {
    mainPadding: 3,
    titleFontSize: '12px',
    width: '100%',
    height: '112px',
    titleHeight: '16px',
    titleLineHeight: '18px',
    propertiesCharLimit: 50,
    propertiesFontSize: 11,
    statusFontSize: 11,
    expiryMargin: 4,
    cardSize: OfferCardSize.NANO,
  },
  [OfferCardSize.SM]: {
    mainPadding: 3,
    titleFontSize: '15px',
    width: '164px',
    height: '224px',
    titleHeight: '51.5px',
    titleLineHeight: '19px',
    propertiesCharLimit: 50,
    propertiesFontSize: 11,
    statusFontSize: 11,
    expiryMargin: 4,
    cardSize: OfferCardSize.SM,
  },
  [OfferCardSize.MD]: {
    mainPadding: 4,
    titleFontSize: '15px',
    width: '176px',
    height: '245px',
    titleHeight: '54.5px',
    titleLineHeight: '19px',
    propertiesCharLimit: 50,
    propertiesFontSize: 12,
    statusFontSize: 14,
    expiryMargin: 8,
    cardSize: OfferCardSize.MD,
  },
  [OfferCardSize.LG]: {
    mainPadding: 5,
    titleFontSize: '18px',
    width: '238px',
    height: '328px',
    titleHeight: '66px',
    titleLineHeight: '22px',
    propertiesCharLimit: 50,
    propertiesFontSize: 12,
    statusFontSize: 14,
    expiryMargin: 8,
    cardSize: OfferCardSize.LG,
  },
};

const offerCardThemeHomeScreen: OfferCardTheme = {
  ...offerCardThemeOffersScreen,
  [OfferCardSize.SM]: {
    mainPadding: 3,
    titleFontSize: '15px',
    width: '145px',
    height: '200px',
    titleHeight: '51.5px',
    titleLineHeight: '19px',
    propertiesCharLimit: 50,
    propertiesFontSize: 11,
    statusFontSize: 11,
    expiryMargin: 4,
    cardSize: OfferCardSize.SM,
  },
  [OfferCardSize.MD]: {
    mainPadding: 4,
    titleFontSize: '15px',
    width: '176px',
    height: '260px',
    titleHeight: '54.5px',
    titleLineHeight: '19px',
    propertiesCharLimit: 50,
    propertiesFontSize: 12,
    statusFontSize: 14,
    expiryMargin: 8,
    cardSize: OfferCardSize.MD,
  },
};

export const useOfferCardTheme = (
  routeName: string = Routes.Main.Offers
): OfferCardThemeProperties => {
  const isHomeScreen = routeName === Routes.Main.Home;
  const offerCardTheme = isHomeScreen
    ? offerCardThemeHomeScreen
    : offerCardThemeOffersScreen;
  const breakpoint = useBreakpoint();
  const isNano = isBreakpointDown(breakpoint, BreakpointsEnum.NANO);
  const isMicro = isBreakpointDown(breakpoint, BreakpointsEnum.MICRO);
  const isLarge = isBreakpointDown(breakpoint, BreakpointsEnum.LG);
  const isDesktop = isBreakpointUp(breakpoint, BreakpointsEnum.XL);
  const isXSandDown = isBreakpointDown(breakpoint, BreakpointsEnum.XS);
  const isXLandDown = isBreakpointDown(breakpoint, BreakpointsEnum.XL);
  const isSMandDown = isBreakpointDown(breakpoint, BreakpointsEnum.SM);
  const isListView = useSelector(selectIsListView);
  const listView =
    (isHomeScreen ? isXSandDown : isNano) || (isListView && !isDesktop);

  if (listView) {
    return offerCardTheme.NANO;
  }

  if (isMicro) {
    return offerCardTheme.SM;
  }

  if (isHomeScreen && isSMandDown) {
    return offerCardTheme.SM;
  }

  if (isLarge) {
    return offerCardTheme.MD;
  }

  if (isHomeScreen && isXLandDown) {
    return offerCardTheme.MD;
  }

  return offerCardTheme.LG;
};
